import RedFlag from "../../entities/redFlag";

/**
 * RedFlag actions
 *
 * Call order:
 *
 * REDFLAG_SAVE_QUEUE:	adds the item to the save queue
 * --> REDFLAG_SAVE:	modifies the state
 * --> REDFLAG_SAVE_SUCCESS: if all went well
 * --> REDFLAG_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail REDFLAG_SAVE_QUEUE is during the call to REDFLAG_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at REDFLAG_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: REDFLAG_SAVE_SUCCESS, REDFLAG_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or REDFLAG_PENDING_CHECK (it left the queue).
 */

export const REDFLAG_FETCH_ONE_QUEUE: any = 'REDFLAG_FETCH_ONE_QUEUE';
export const REDFLAG_FETCH_SUCCESS: any = 'REDFLAG_FETCH_SUCCESS';
export const REDFLAG_FETCH_FAILURE: any = 'REDFLAG_FETCH_FAILURE';
export const REDFLAG_FETCH_ONE_SUCCESS: any = 'REDFLAG_FETCH_ONE_SUCCESS';
export const REDFLAG_FETCH_ONE_FAILURE: any = 'REDFLAG_FETCH_ONE_FAILURE';

export const REDFLAG_SAVE: any = 'REDFLAG_SAVE';
export const REDFLAG_SAVE_QUEUE: any = 'REDFLAG_SAVE_QUEUE';
export const REDFLAG_SAVE_SUCCESS: any = 'REDFLAG_SAVE_SUCCESS';
export const REDFLAG_SAVE_FAILURE: any = 'REDFLAG_SAVE_FAILURE';

export const REDFLAG_SAVE_MANY: any = 'REDFLAG_SAVE_MANY';
export const REDFLAG_SAVE_MANY_QUEUE: any = 'REDFLAG_SAVE_MANY_QUEUE';
export const REDFLAG_SAVE_MANY_SUCCESS: any = 'REDFLAG_SAVE_MANY_SUCCESS';
export const REDFLAG_SAVE_MANY_FAILURE: any = 'REDFLAG_SAVE_MANY_FAILURE';

export const REDFLAG_REMOVE: any = 'REDFLAG_REMOVE';
export const REDFLAG_REMOVE_QUEUE: any = 'REDFLAG_REMOVE_QUEUE';
export const REDFLAG_REMOVE_SUCCESS: any = 'REDFLAG_REMOVE_SUCCESS';
export const REDFLAG_REMOVE_FAILURE: any = 'REDFLAG_REMOVE_FAILURE';

export const REDFLAG_RESTORE: any = 'REDFLAG_RESTORE';
export const REDFLAG_RESTORE_QUEUE: any = 'REDFLAG_RESTORE_QUEUE';
export const REDFLAG_RESTORE_SUCCESS: any = 'REDFLAG_RESTORE_SUCCESS';
export const REDFLAG_RESTORE_FAILURE: any = 'REDFLAG_RESTORE_FAILURE';

export const REDFLAG_RESTORE_MANY: any = 'REDFLAG_RESTORE_MANY';
export const REDFLAG_REMOVE_MANY: any = 'REDFLAG_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const REDFLAG_SAVE_REVERT: any = 'REDFLAG_SAVE_REVERT';
export const REDFLAG_PENDING_CHECK: any = 'REDFLAG_PENDING_CHECK';

/* Fetch actions */
export const fetchRedFlagSuccess = (redFlags: RedFlag[], partial: boolean, lastFetch: number) => ({type: REDFLAG_FETCH_SUCCESS, redFlags, partial, lastFetch});
export const fetchRedFlagFailure = (err: any) => {
		console.error('RedFlagAction: fetchRedFlagFailure', err);
		return {type: REDFLAG_FETCH_FAILURE, err};
};
export const fetchOneRedFlagQueue = (redFlagId: string, sentAddedOn?: number) => ({type: REDFLAG_FETCH_ONE_QUEUE, redFlagId, sentAddedOn});
export const fetchOneRedFlagSuccess = (redFlag: RedFlag | null, sentAddedOn?: number, redFlagId: string = redFlag?.id) => ({type: REDFLAG_FETCH_ONE_SUCCESS, redFlagId, redFlag, sentAddedOn});
export const fetchOneRedFlagFailure = (id: string, err: any) => {
		console.error('RedFlagAction: fetchOneRedFlagFailure', err);
		return {type: REDFLAG_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveRedFlag = (redFlag: RedFlag) => {
    // console.debug('⮂ REDFLAG_SAVE action', redFlag?.id);
    return {type: REDFLAG_SAVE, redFlag};
}
export const saveRedFlagQueue = (redFlag: RedFlag, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ REDFLAG_SAVE_QUEUE action', redFlag?.id);
    return {type: REDFLAG_SAVE_QUEUE, redFlag, onQueued};
}
export const saveRedFlagSuccess = (redFlag: RedFlag, sentAddedOn?: number) => {
    // console.debug('⮂ REDFLAG_SAVE_SUCCESS action', redFlag?.id);
    return {type: REDFLAG_SAVE_SUCCESS, redFlag, sentAddedOn};
}
export const saveRedFlagFailure = (redFlag: RedFlag, status: number, message: string) => {
    console.warn(`⮂ REDFLAG_SAVE_FAILURE error saving redFlag: HTTP ${status}: ${message}`, redFlag?.id);
    return {type: REDFLAG_SAVE_FAILURE, redFlag, status, message};
};

/* SaveMany actions */
export const saveManyRedFlags = (redFlags: RedFlag[]) => {
    // console.debug('⮂ REDFLAG_SAVE_MANY action', redFlags?.map(redFlag => redFlag?.id).join(','));
    return {type: REDFLAG_SAVE_MANY, redFlags};
}
export const saveManyRedFlagsQueue = (redFlags: RedFlag[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ REDFLAG_SAVE_MANY_QUEUE action', redFlags?.map(redFlag => redFlag.id).join(','));
    return {type: REDFLAG_SAVE_MANY_QUEUE, redFlags, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `redFlags` (OWA-1018) */
export const saveManyRedFlagsSuccess = (redFlags: RedFlag[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ REDFLAG_SAVE_MANY_SUCCESS action', redFlags?.map(redFlag => redFlag.id).join(','));
    return {type: REDFLAG_SAVE_MANY_SUCCESS, redFlags, sentAddedOns};
}
export const saveManyRedFlagsFailure = (redFlags: RedFlag[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_REDFLAGS_FAILURE Error saving redFlags: HTTP ${status}: ${message}`, redFlags?.map(redFlag => redFlag.id).join(','));
		return {type: REDFLAG_SAVE_MANY_FAILURE, redFlags, status, message};
};

/* Remove actions */
export const removeRedFlag = (redFlag: RedFlag) => ({ type: REDFLAG_REMOVE, redFlag });
export const removeRedFlagQueue = (redFlag: RedFlag) => ({ type: REDFLAG_REMOVE_QUEUE, redFlag });
export const removeRedFlagSuccess = (redFlag: RedFlag) => ({ type: REDFLAG_REMOVE_SUCCESS, redFlag });
export const removeRedFlagFailure = (redFlag: RedFlag, status: number, message: string) => {
	console.error(`Redux: removeRedFlagFailure Error removing redFlag: HTTP ${status}: ${message}`);
	return ({ type: REDFLAG_REMOVE_FAILURE, redFlag, status, message });
};

/* Restore actions */
export const restoreRedFlag = (redFlag: RedFlag) => ({ type: REDFLAG_RESTORE, redFlag });
export const restoreRedFlagQueue = (redFlag: RedFlag) => ({ type: REDFLAG_RESTORE_QUEUE, redFlag });
export const restoreRedFlagSuccess = (redFlag: RedFlag, sentAddedOn?: number) => ({ type: REDFLAG_RESTORE_SUCCESS, redFlag, sentAddedOn });
export const restoreRedFlagFailure = (redFlag: RedFlag, status: number, message: string) => {
	console.error(`Redux: restoreRedFlagFailure Error restoring redFlag: HTTP ${status}: ${message}`);
	return ({ type: REDFLAG_RESTORE_FAILURE, redFlag, status, message });
};

/* RestoreMany actions */
export const restoreManyRedFlags = (redFlags: RedFlag[]) => ({ type: REDFLAG_RESTORE_MANY, redFlags });

/* RemoveMany actions */
export const removeManyRedFlags = (redFlags: RedFlag[]) => ({ type: REDFLAG_REMOVE_MANY, redFlags });

/* Pending save actions (OWA-1018) */
/**
 * The save `redFlag` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertRedFlagSave = (redFlag: RedFlag, options: {sentAddedOn?: number, current?: RedFlag | null, keep?: boolean} = {}) =>
    ({type: REDFLAG_SAVE_REVERT, redFlag, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkRedFlagPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: REDFLAG_PENDING_CHECK, live, refused, readAt});